import React, { useState, useEffect } from 'react';
import { Package, TrendingDown, TrendingUp, Download, AlertCircle } from 'lucide-react';
import { getDriverDashboard, updateDriverInventory, downloadDriverInventory } from '@/api/drivers';
import { Button, Loading, ErrorMessage } from '@/components/common';
import { formatCurrency } from '@/utils/formatters';
import { useAuthStore } from '@/stores/authStore';

interface BatteryInventoryItem {
  batteryId: number;
  size: string;
  brand: string;
  tradeName?: string;
  salePrice: number;
  suppliers: {
    FK?: number;
    M?: number;
    VARTA?: number;
    EMTRAC?: number;
  };
}

export const DriverDashboardPage: React.FC = () => {
  useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [driverData, setDriverData] = useState<any>(null);
  const [selectedBattery, setSelectedBattery] = useState<BatteryInventoryItem | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [action, setAction] = useState<'sold' | 'return'>('sold');
  const [quantity, setQuantity] = useState(1);
  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDriverDashboard();
      setDriverData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'فشل في تحميل لوحة التحكم');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInventory = async () => {
    if (!selectedBattery || !selectedSupplier) {
      setUpdateMessage({ type: 'error', text: 'الرجاء اختيار البطارية والمورد' });
      return;
    }

    const supplierIds: Record<string, number> = { FK: 1, M: 2, VARTA: 3, EMTRAC: 4 };
    const supplierId = supplierIds[selectedSupplier];

    try {
      setUpdating(true);
      setUpdateMessage(null);
      await updateDriverInventory({
        batteryId: selectedBattery.batteryId,
        supplierId,
        action,
        quantity
      });
      
      setUpdateMessage({ 
        type: 'success', 
        text: action === 'sold' ? 'تم تسجيل البيع بنجاح' : 'تمت الإرجاع للمستودع بنجاح'
      });
      
      // Reload dashboard
      await loadDashboard();
      
      // Reset form
      setSelectedBattery(null);
      setSelectedSupplier('');
      setQuantity(1);
    } catch (err: any) {
      setUpdateMessage({ 
        type: 'error', 
        text: err.response?.data?.error?.message || 'فشل في تحديث المخزون'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleExport = async () => {
    if (driverData?.driver) {
      try {
        await downloadDriverInventory(driverData.driver.id, driverData.driver.englishName);
      } catch (err) {
        setError('فشل في تصدير الملف');
      }
    }
  };

  if (loading) return <Loading text="جاري تحميل لوحة التحكم..." />;
  if (error) return <ErrorMessage message={error} />;
  if (!driverData) return null;

  const { driver, totalInventory, inventoryBySupplier, batteryInventory } = driverData;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary">لوحة تحكم السائق</h1>
          <p className="text-gray-400 mt-1">{driver.arabicName} ({driver.englishName})</p>
        </div>
        <Button onClick={handleExport} className="gap-2">
          <Download className="h-5 w-5" />
          تصدير إلى Excel
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card bg-gradient-to-br from-blue-600 to-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-sm">المخزون الكلي</p>
              <p className="text-3xl font-bold text-white mt-1">{totalInventory}</p>
            </div>
            <Package className="h-12 w-12 text-blue-300 opacity-50" />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-600 to-green-800">
          <div>
            <p className="text-green-200 text-sm">FK</p>
            <p className="text-2xl font-bold text-white mt-1">{inventoryBySupplier.FK || 0}</p>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-600 to-purple-800">
          <div>
            <p className="text-purple-200 text-sm">M</p>
            <p className="text-2xl font-bold text-white mt-1">{inventoryBySupplier.M || 0}</p>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-600 to-yellow-800">
          <div>
            <p className="text-yellow-200 text-sm">VARTA</p>
            <p className="text-2xl font-bold text-white mt-1">{inventoryBySupplier.VARTA || 0}</p>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-600 to-red-800">
          <div>
            <p className="text-red-200 text-sm">EMTRAC</p>
            <p className="text-2xl font-bold text-white mt-1">{inventoryBySupplier.EMTRAC || 0}</p>
          </div>
        </div>
      </div>

      {/* Update Inventory Form */}
      <div className="card">
        <h2 className="text-xl font-bold text-primary mb-4">تحديث المخزون</h2>
        
        {updateMessage && (
          <div className={`mb-4 p-4 rounded-lg ${
            updateMessage.type === 'success' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'
          }`}>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <span>{updateMessage.text}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">البطارية</label>
            <select
              className="input w-full"
              value={selectedBattery?.batteryId || ''}
              onChange={(e) => {
                const battery = batteryInventory.find((b: BatteryInventoryItem) => 
                  b.batteryId === parseInt(e.target.value)
                );
                setSelectedBattery(battery || null);
                setSelectedSupplier('');
              }}
            >
              <option value="">اختر البطارية</option>
              {batteryInventory.map((battery: BatteryInventoryItem) => (
                <option key={battery.batteryId} value={battery.batteryId}>
                  {battery.brand} - {battery.size} {battery.tradeName ? `(${battery.tradeName})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">المورد</label>
            <select
              className="input w-full"
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              disabled={!selectedBattery}
            >
              <option value="">اختر المورد</option>
              {selectedBattery && Object.entries(selectedBattery.suppliers).map(([code, qty]) => 
                qty && qty > 0 ? (
                  <option key={code} value={code}>
                    {code} ({qty} قطعة)
                  </option>
                ) : null
              )}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">العملية</label>
            <select
              className="input w-full"
              value={action}
              onChange={(e) => setAction(e.target.value as 'sold' | 'return')}
            >
              <option value="sold">بيع</option>
              <option value="return">إرجاع للمستودع</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">الكمية</label>
            <input
              type="number"
              min="1"
              className="input w-full"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            />
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <Button
            onClick={handleUpdateInventory}
            disabled={!selectedBattery || !selectedSupplier || updating}
            className="gap-2"
          >
            {action === 'sold' ? (
              <>
                <TrendingDown className="h-5 w-5" />
                تسجيل بيع
              </>
            ) : (
              <>
                <TrendingUp className="h-5 w-5" />
                إرجاع للمستودع
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="card">
        <h2 className="text-xl font-bold text-primary mb-4">المخزون التفصيلي</h2>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>الماركة</th>
                <th>الحجم</th>
                <th>الاسم التجاري</th>
                <th>FK</th>
                <th>M</th>
                <th>VARTA</th>
                <th>EMTRAC</th>
                <th>المجموع</th>
                <th>سعر البيع</th>
              </tr>
            </thead>
            <tbody>
              {batteryInventory.map((battery: BatteryInventoryItem) => {
                const total = (battery.suppliers.FK || 0) + 
                             (battery.suppliers.M || 0) + 
                             (battery.suppliers.VARTA || 0) + 
                             (battery.suppliers.EMTRAC || 0);
                
                return (
                  <tr key={battery.batteryId}>
                    <td className="font-semibold">{battery.brand}</td>
                    <td className="font-medium">{battery.size}</td>
                    <td className="text-sm text-gray-400">{battery.tradeName || '-'}</td>
                    <td className="text-center">{battery.suppliers.FK || 0}</td>
                    <td className="text-center">{battery.suppliers.M || 0}</td>
                    <td className="text-center">{battery.suppliers.VARTA || 0}</td>
                    <td className="text-center">{battery.suppliers.EMTRAC || 0}</td>
                    <td className="font-bold text-center">{total}</td>
                    <td className="font-medium text-green-400">{formatCurrency(battery.salePrice)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
